/*
,------.                                    ,--.          ,--.      ,--------.    ,------.            ,--.           
|  .-.  \,--. ,--.,--,--,  ,--,--.,--,--,--.`--' ,---.    |  | ,---.'--.  .--'    |  .-.  \  ,--,--.,-'  '-. ,--,--. 
|  |  \  :\  '  / |      \' ,-.  ||        |,--.| .--'    |  || .-. |  |  |       |  |  \  :' ,-.  |'-.  .-'' ,-.  | 
|  '--'  / \   '  |  ||  |\ '-'  ||  |  |  ||  |\ `--.    |  |' '-' '  |  |       |  '--'  /\ '-'  |  |  |  \ '-'  | 
`-------'.-'  /   `--''--' `--`--'`--`--`--'`--' `---'    `--' `---'   `--'       `-------'  `--`--'  `--'   `--`--' 
         `---'                                                                                                      
*/
import html2canvas from 'html2canvas';
import { TimeSelect } from '../components/timeselect';
import { BaseService } from '../service/base';
import { VisionWindowManager } from './manager';
import { VisionWindow } from './window';
import { Measurement } from '../components/measurement';
import { FormElements } from '../components/form';
import { formatDateTime, VisionApplication } from '../application';

/** Define the interface to communicate with the server request an area search. */
interface AreaSearchRequestDTO {
    northEastLat: number;
    northEastLng: number;
    southWestLat: number;
    southWestLng: number;
    startTime: string;
    endTime: string;
    imgData: string;
    groupId: string | null;
}

/** Define the interface for the area search response */
interface AreaSearchResponseDTO {
    in: string;
    out: string;
    assetName: string;
    assetId: string;
    data: AreaSearchDTO[];

    // This data is only used on the client side to store graphical details.
    polyline: google.maps.Polyline | null;
    markers: google.maps.Marker[] | null;
}

class SearchRow {
    in!: number;
    out!: number;
    assetName!: string;
    maxSpeed!: number;
    activity = new Set<string>();

    data!: AreaSearchDTO[];
    polyline!: google.maps.Polyline | null;
    markers!: google.maps.Marker[] | null;
}

interface AreaSearchDTO {
    timestamp: number;
    lat: number;
    lon: number;
    input1: boolean;
    input2: boolean;
    input3: boolean;
    input4: boolean;
    heading: number;
    speed: number;
}

interface AreaSearchResponseReportDTO {
    name: string;
    in: string;
    out: string;
    activity: string;
    maxSpeed: string;
}

/** Handles communication with the server to area search queries.
 *
 */
class AreaSearchService extends BaseService {
    async getSummaryReport(tableData: AreaSearchResponseReportDTO[], format: string) {
        if (format == 'export_xls') {
            this.downloadXLSXPost('/areasearch/report?format=xlsx', 'Area-Search Summary.xlsx', tableData, () => {
                /* empty */
            });
        } else if (format == 'export_pdf') {
            this.downloadPDFPost('/areasearch/report?format=pdf', 'Area-Search Summary.pdf', tableData, () => {
                /* empty */
            });
        }
    }

    async pdfReport(request: AreaSearchRequestDTO, imgData: string) {
        request.imgData = imgData;

        const args = {
            method: 'post',
            body: JSON.stringify(request),
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + this.jwt,
            },
        };

        const name = 'AreaSearch-Report.pdf';
        fetch(this.url + '/api/' + this.orgid + '/areasearch/report/pdf', args)
            .then((response) => {
                return response.blob();
            })
            .then((blob) => {
                const url = URL.createObjectURL(blob);
                // Optional: Step 5b: Create a link to download the PDF
                const link = document.createElement('a');
                link.href = url;
                link.download = name;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                // Free up the URL object
                URL.revokeObjectURL(url);
            });
    }

    /** Request a search results.
     *
     * @param request The DTO request to search
     * @returns Promise to the result.
     */
    async search(request: AreaSearchRequestDTO) {
        const response = await this.post<AreaSearchResponseDTO[]>(this.url + '/api/' + this.orgid + '/areasearch', request);
        return response;
    }
}

export class VisionAreaSearchWindow extends VisionWindow {
    layout!: dhtmlXLayoutObject;
    resultsGrid!: dhtmlXGridObject;
    paramForm!: dhtmlXForm;
    private resultsToolbar!: dhtmlXToolbarObject;
    //area!: google.maps.Polygon;
    area!: google.maps.Rectangle;
    service: AreaSearchService;
    private resultsCache!: Map<number, AreaSearchResponseDTO>;
    private activeRow: number;
    private rowCache!: Map<number, SearchRow>;

    constructor(manager: VisionWindowManager) {
        const width = window.innerWidth * 0.5;

        super(manager, {
            id: 'area_search_window',
            title: 'Area Search',
            x: 30,
            y: 30,
            width: width,
            height: 600,
        });

        this.service = new AreaSearchService();
        this.resultsCache = new Map<number, AreaSearchResponseDTO>();
        this.rowCache = new Map<number, SearchRow>();
        this.activeRow = -1;
        this.window.setText('Area Search');
        this.visible = true;

        this.manager.win.window('area_search_window').attachEvent('onClose', () => {
            if (this.area != undefined) {
                this.area.setMap(null);
                this.manager.app.layout.avl2Tab.mapPanel.stopDrawing();
                this.clearResults();
            }
            this.visible = false;
            return true;
        });

        this.layout = this.window.attachLayout('2E');
        this.layout.cells('a').setText('Search Parameters');
        this.layout.cells('b').setText('Results');
        this.resultsToolbar = this.layout.cells('b').attachToolbar();

        this.resultsToolbar.addButton('export_xls', 1, 'Summary', 'icons/excel.png', '');
        this.resultsToolbar.addButton('export_pdf', 2, 'Summary', 'icons/pdf.png', '');

        const formData = JSON.parse(JSON.stringify(TimeSelect.FORM));

        formData.push({
            type: 'block',
            list: [FormElements.button('search', 'Search', 'search')],
        });

        this.paramForm = this.layout.cells('a').attachForm(formData);
        this.paramForm.getInput(TimeSelect.START).autocomplete = 'off';
        this.paramForm.getInput(TimeSelect.END).autocomplete = 'off';
        this.paramForm.hideItem('download');

        this.resultsGrid = this.layout.cells('b').attachGrid();
        this.resultsGrid.setHeader('Name,Enter,Exit,Activity,Max,Details,Details', '', [
            'text-align:center',
            'text-align:center',
            'text-align:center',
            'text-align:center',
            'text-align:center',
            'text-align:center',
            'text-align:center',
        ]);
        this.resultsGrid.setColTypes('ro,ro,ro,ro,ro,ro,ro');
        this.resultsGrid.setColAlign('left,center,center,center,center,center,center');
        this.resultsGrid.init();

        this.setupEvents();
        this.populateTime(TimeSelect.TODAY);
    }

    /** UI/UX Events
     *
     * Contains 3 Trigger Events:
     *  1)  User select a row in the results grid that will draw the track on the map
     *  2)  User selects a time range in the pull down to populate the start/stop times
     *  3)  User presses the Search or Save Map buttons to search results or save the map to pdf
     */
    setupEvents() {
        // Grid Select
        this.resultsGrid.attachEvent('onRowSelect', (id, index) => {
            this.gridRowSelectEvent(id, index);
        });

        // Form Time Select
        this.paramForm.attachEvent('onChange', (name, value, _state) => {
            if (name == TimeSelect.SELECT) {
                this.populateTime(value);
            }
        });

        // Form Button Press
        this.paramForm.attachEvent('onButtonClick', (id) => {
            if (id == 'search') {
                this.performSearch();
            } else if (id == 'download') {
                //        this.getSummaryReport();
            }
        });

        this.resultsToolbar.attachEvent('onClick', (id) => {
            this.getSummaryReport(id);
        });
    }

    private getSummaryReport(id: string) {
        const requestData = Array<AreaSearchResponseReportDTO>();

        this.resultsGrid.forEachRow((rid) => {
            const data = {} as AreaSearchResponseReportDTO;

            data.name = this.resultsGrid.cells(rid, 0).getValue();
            data.in = this.resultsGrid.cells(rid, 1).getValue();
            data.out = this.resultsGrid.cells(rid, 2).getValue();
            data.activity = this.resultsGrid.cells(rid, 3).getValue();
            data.maxSpeed = this.resultsGrid.cells(rid, 4).getValue();
            requestData.push(data);
        });

        this.service.getSummaryReport(requestData, id);
    }

    /** Save whatever the map div has displayed to a PDF
     *
     *
     */
    private downloadReport(request: AreaSearchRequestDTO): void {
        const element = document.getElementById('map_panel_id') as HTMLElement;

        html2canvas(element, {
            useCORS: true,
            allowTaint: false,
        }).then((canvas) => {
            //const imgWidth = element.clientWidth;
            //const imgHeight = element.clientHeight;
            this.service.pdfReport(request, canvas.toDataURL().split(';base64,')[1]);
        });
    }

    private processResponse(data: AreaSearchResponseDTO[]): void {
        let rowId = 1;
        this.resultsGrid.clearAll(false);
        this.clearResults();

        const buttonHTMLPDF = `
                <button style="
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    padding: 4px 8px;
                    border: 1px solid #ccc;
                    border-radius: 4px;
                    background-color: #f0f0f0;
                    cursor: pointer;
                    ">
                    <img src="icons/pdf.png" alt="Icon" width="16" height="16">                    
                </button>`;

        const buttonHTMLXLS = `
                <button style="
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    padding: 4px 8px;
                    border: 1px solid #ccc;
                    border-radius: 4px;
                    background-color: #f0f0f0;
                    cursor: pointer;
                    ">
                    <img src="icons/excel.png" alt="Icon" width="16" height="16">                    
                </button>`;

        for (const item of data) {
            const assetName = VisionApplication.getInstance().assetService.assetList.get(item.assetId)?.id.name;
            const assetId = item.assetId;

            let currentTimeStamp = 0;
            let currentRow = undefined;

            for (const telem of item.data) {
                console.log('Time Difference is:');
                console.log(telem.timestamp - currentTimeStamp);

                const act1 = VisionApplication.getInstance().assetService.getActivityForInput(assetId, 1, telem.input1);
                const act2 = VisionApplication.getInstance().assetService.getActivityForInput(assetId, 2, telem.input2);
                const act3 = VisionApplication.getInstance().assetService.getActivityForInput(assetId, 3, telem.input3);
                const act4 = VisionApplication.getInstance().assetService.getActivityForInput(assetId, 4, telem.input4);

                if (Math.abs(telem.timestamp - currentTimeStamp) > 300000) {
                    if (currentRow != undefined) {
                        const inTime = formatDateTime(currentRow.in);
                        const outTime = formatDateTime(currentRow.out);
                        if (act1) currentRow.activity.add(act1);
                        if (act2) currentRow.activity.add(act2);
                        if (act3) currentRow.activity.add(act3);
                        if (act4) currentRow.activity.add(act4);
                        const insertData = [assetName, inTime, outTime, Array.from(currentRow.activity).join(' '), Measurement.km2miles(currentRow.maxSpeed) + 'mph', buttonHTMLPDF, buttonHTMLXLS];
                        this.resultsGrid.addRow(rowId, insertData, 0, 0, '', false);
                        this.resultsGrid.setUserData(rowId, 'asset_id', assetId);
                        this.rowCache.set(rowId, currentRow);
                        rowId = rowId + 1;
                    }
                    currentRow = new SearchRow();
                    currentRow.out = telem.timestamp;
                    currentRow.data = [];
                    currentRow.maxSpeed = telem.speed;
                }
                if (currentRow) {
                    currentRow.in = telem.timestamp;
                    if (act1) currentRow.activity.add(act1);
                    if (act2) currentRow.activity.add(act2);
                    if (act3) currentRow.activity.add(act3);
                    if (act4) currentRow.activity.add(act4);

                    currentRow.data.push(telem);
                    if (telem.speed > currentRow.maxSpeed) {
                        currentRow.maxSpeed = telem.speed;
                    }
                }
                currentTimeStamp = telem.timestamp;
            }

            if (currentRow != undefined) {
                const inTime = formatDateTime(currentRow.in);
                const outTime = formatDateTime(currentRow.out);
                const insertData = [assetName, inTime, outTime, Array.from(currentRow.activity).join(' '), Measurement.km2miles(currentRow.maxSpeed) + 'mph', buttonHTMLPDF, buttonHTMLXLS];
                this.resultsGrid.addRow(rowId, insertData, 0, 0, '', false);
                this.resultsGrid.setUserData(rowId, 'asset_id', assetId);
                this.rowCache.set(rowId, currentRow);
                rowId += 1;
            }
            this.resultsGrid.setSizes();
        }
    }

    /** Processes the Area Search data from the server and inserts it into the data table
     *
     *  @param data The response DTO object from the server.  We will also cache this
     *
     */
    private _processResponse(data: AreaSearchResponseDTO[]): void {
        const rowid = 1;
        this.resultsGrid.clearAll(false);
        this.clearResults();
        this.resultsCache.forEach((item) => {
            item.polyline?.setMap(null);
            item.markers?.forEach((marker) => {
                marker.setMap(null);
            });
        });
        this.resultsCache.clear();

        if (data.length == 0) {
            dhtmlx.message({
                title: 'No Data',
                text: 'No equipment found in the area for that time.',
                type: 'alert-error',
            });
        }

        for (const item of data) {
            const assetName = VisionApplication.getInstance().assetService.assetList.get(item.assetId)?.id.name;
            const assetId = item.assetId;

            const dateIn = formatDateTime(item.in);
            const dateOut = formatDateTime(item.out);

            const activity = new Set<string>();
            const maxSpeed = 0;

            /*
            for (const telem of item.data) {
                const act1 = VisionApplication.getInstance().assetService.getActivityForInput(telem.assetId, 1, telem.input1);
                if (act1 != undefined) {
                    activity.add(act1);
                }

                if (telem.speed > maxSpeed) {
                    maxSpeed = telem.speed;
                }

                const act2 = VisionApplication.getInstance().assetService.getActivityForInput(telem.assetId, 2, telem.input2);
                if (act2 != undefined) {
                    activity.add(act2);
                }

                const act3 = VisionApplication.getInstance().assetService.getActivityForInput(telem.assetId, 3, telem.input3);
                if (act3 != undefined) {
                    activity.add(act3);
                }

                const act4 = VisionApplication.getInstance().assetService.getActivityForInput(telem.assetId, 4, telem.input4);
                if (act4 != undefined) {
                    activity.add(act4);
                }
            }

            //var dateIn = (new Date(data[i].in).toLocaleDateString('en-us', { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "numeric", second: "numeric" }));
            //var dateOut = (new Date(data[i].out).toLocaleDateString('en-us', { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "numeric", second: "numeric" }));

            const buttonHTML = `
                <button style="
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    padding: 4px 8px;
                    border: 1px solid #ccc;
                    border-radius: 4px;
                    background-color: #f0f0f0;
                    cursor: pointer;
                    ">
                    <img src="icons/inspect.png" alt="Icon" width="16" height="16">
                    
                </button>`;
            */
            //const insertData = [assetName, dateIn, dateOut, Array.from(activity).join(' '), Measurement.km2miles(maxSpeed) + 'mph', buttonHTML];
            //this.resultsGrid.addRow(rowid, insertData, 0, '', '', false);
            //this.resultsGrid.setUserData(rowid, 'asset_id', assetId);
            //this.resultsCache.set(rowid, item);
            //rowid++;
        }

        this.layout.cells('b').setText('Results (' + data.length + ' found)');
    }

    /** When the map drawing manager has finished it will call this to set the area to use for the search.
     *
     * @param area The polygon area to search
     *
     */
    setArea(area: google.maps.Rectangle) {
        this.area = area;
    }

    hide() {
        super.hide();
        this.area.setMap(null);
    }

    /** Clear the grid and any polygon/markers from the map.
     *
     *
     */
    private clearResults(): void {
        if (this.activeRow != -1) {
            const oldTrack = this.rowCache.get(this.activeRow);
            oldTrack?.polyline?.setMap(null);
            oldTrack?.markers?.forEach((marker) => {
                marker.setMap(null);
            });
        }
    }

    private downloadDetails(assetid: string, data: SearchRow, format: 'pdf' | 'xlsx'): void {
        const start = new Date(data.in).getTime();
        const end = new Date(data.out).getTime();
        if (format === 'xlsx') {
            this.service.downloadXLSX('/areasearch/details?format=xlsx&start=' + start + '&end=' + end + '&assetid=' + assetid, 'AreaDetails.xlsx');
        } else if (format === 'pdf') {
            this.service.downloadPDF('/areasearch/details?format=pdf&start=' + start + '&end=' + end + '&assetid=' + assetid, 'AreaDetails.pdf');
        }

        const element = document.getElementById('map_panel_id') as HTMLElement;
        html2canvas(element, {
            useCORS: true,
            allowTaint: false,
        }).then((canvas) => {
            const a = document.createElement('a');
            a.href = canvas.toDataURL('image/png');
            a.download = 'area-search-map.png';
            a.click();
        });
    }

    /** Fired when user clicks on a row in the result grid.
     *
     *
     */
    private gridRowSelectEvent(id: number, index: number): void {
        // User has clicked the View Telemetry Button

        console.log(id);
        const data = this.rowCache.get(id);
        if (data == undefined) {
            return;
        }

        //this.resultsGrid.clearSelection();
        //this.resultsGrid.selectRow(id, false, false, true);

        this.clearResults();
        let aid = this.resultsGrid.getUserData(id, 'asset_id');
        if (index == 5) {
            aid = this.resultsGrid.getUserData(id, 'asset_id');
            this.downloadDetails(aid, data, 'pdf');
        } else if (index == 6) {
            aid = this.resultsGrid.getUserData(id, 'asset_id');
            this.downloadDetails(aid, data, 'xlsx');
        }

        let speedValue = 0;
        let speedCount = 0;

        if (data.polyline == null) {
            const path = [];
            data.markers = new Array<google.maps.Marker>();
            console.log();

            for (const item of data.data) {
                console.log(item.heading);
                path.push({ lat: item.lat, lng: item.lon });

                speedValue += item.speed;
                speedCount += 1;

                let colour = '#00AA00';
                if (item.input1 == true || item.input2 == true || item.input3 == true || item.input4 == true) {
                    colour = '#0000FF';
                }
                const marker = new google.maps.Marker({
                    position: { lat: item.lat, lng: item.lon },
                    icon: {
                        path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                        rotation: item.heading,
                        scale: 3, // Adjust the size of the arrow
                        fillColor: colour,
                        fillOpacity: 1, // Arrow opacity
                        strokeColor: '#FFFFFF', // Arrow border color
                        strokeWeight: 1, // Arrow border weight
                    },
                });
                data.markers.push(marker);

                const infoWindow = new google.maps.InfoWindow({
                    content: '',
                    pixelOffset: new google.maps.Size(8, -32),
                });

                marker.addListener('mouseover', () => {
                    console.log('Mouse Over Marker');
                    console.log(marker);
                    infoWindow.setPosition(marker.getPosition());
                    infoWindow.open(this.manager.app.layout.avl2Tab.mapPanel.map);

                    const act1 = this.manager.app.assetService.getActivityForInput(aid, 1, item.input1);
                    const act2 = this.manager.app.assetService.getActivityForInput(aid, 2, item.input2);
                    const act3 = this.manager.app.assetService.getActivityForInput(aid, 3, item.input3);
                    const act4 = this.manager.app.assetService.getActivityForInput(aid, 4, item.input4);

                    const content = `
<div class="info-card">
  <h3>Vehicle Info</h3>
  <div class="info-row">
    <span class="label">Date:</span>
    <span class="value">${formatDateTime(item.timestamp)}</span>
  </div>
  <div class="info-row">
    <span class="label">Speed:</span>
    <span class="value">${Measurement.km2miles(item.speed)} mph (${Measurement.km2miles(speedValue / speedCount)} mph avg)</span>
  </div>


  <div class="inputs-section">
    <h4>Inputs</h4>
    <table class="inputs-table">
      <thead>
        <tr>
          <th>1</th>
          <th>2</th>
          <th>3</th>
          <th>4</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>${act1 ?? 'Off'}</td>
          <td>${act2 ?? 'Off'}</td>
          <td>${act3 ?? 'Off'}</td>
          <td>${act4 ?? 'Off'}</td>
        </tr>
      </tbody>
    </table>
  </div>
</div>
`;

                    infoWindow.setContent(content);
                });

                marker.addListener('mouseout', () => {
                    console.log('Mover OUT');
                    infoWindow.close();
                });
            }
            data.polyline = new google.maps.Polyline({
                path: path,
                geodesic: true,
                strokeColor: '#FF0000',
                strokeOpacity: 1.0,
                strokeWeight: 2,
            });
        }

        const map = this.manager.app.layout.avl2Tab.mapPanel.map;
        if (map) {
            data.polyline.setMap(map.map);
            data.markers?.forEach((marker) => {
                marker.setMap(map.map);
            });
        }
        this.activeRow = id;

        /*
        console.log(this.resultsCache.get(id));
        const data = this.resultsCache.get(id);


        if (data == undefined) {
            return;
        }

        if (index == 5) {
            const aid = this.resultsGrid.getUserData(id, 'asset_id');
            this.downloadDetails(aid, data);
        }

        this.clearResults();
        if (data.polyline == null) {
            const path = [];
            data.markers = new Array<google.maps.Marker>();
            console.log();

            for (const item of data.data) {
                console.log(item.heading);
                path.push({ lat: item.lat, lng: item.lon });
                const marker = new google.maps.Marker({
                    position: { lat: item.lat, lng: item.lon },
                    icon: {
                        path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                        rotation: item.heading,
                        scale: 3, // Adjust the size of the arrow
                        fillColor: '#FF0000', // Arrow color
                        fillOpacity: 1, // Arrow opacity
                        strokeColor: '#FFFFFF', // Arrow border color
                        strokeWeight: 1, // Arrow border weight
                    },
                });
                data.markers.push(marker);
            }
            data.polyline = new google.maps.Polyline({
                path: path,
                geodesic: true,
                strokeColor: '#FF0000',
                strokeOpacity: 1.0,
                strokeWeight: 2,
            });
        }

        const map = this.manager.app.layout.avl2Tab.mapPanel.map;
        if (map) {
            data.polyline.setMap(map.map);
            data.markers?.forEach((marker) => {
                marker.setMap(map.map);
            });
        }
        this.activeRow = id;
        */
    }

    /** Request a search.
     *
     *  Builds a request based on the form and area bounds.
     *  Uses the service to make a request to the server for results.
     *  Calls the process function to process the results.
     */
    performSearch() {
        const request = {} as AreaSearchRequestDTO;
        request.startTime = this.paramForm.getItemValue(TimeSelect.START);
        request.endTime = this.paramForm.getItemValue(TimeSelect.END);

        const bounds = this.area.getBounds() as google.maps.LatLngBounds;

        request.northEastLat = bounds.getNorthEast().lat();
        request.northEastLng = bounds.getNorthEast().lng();
        request.southWestLat = bounds.getSouthWest().lat();
        request.southWestLng = bounds.getSouthWest().lng();
        request.groupId = localStorage.getItem('selected_folder');

        this.layout.progressOn();

        const response = this.service.search(request);
        response.then((data) => {
            if (data?.ok) {
                if (data.parsedBody) {
                    this.processResponse(data.parsedBody);
                }
            }
            this.paramForm.showItem('download');
            this.layout.progressOff();
        });
    }

    /** User selected an item in the time period box.
     *  TODO: move this to the TimeSelect class.
     * @param period The time period to use.  See the TimeSelect class for options.
     */
    populateTime(period: string): void {
        const select = new TimeSelect(this.paramForm);
        select.selectRange(period);
    }
}
